import { Grid, Typography, Button, ButtonGroup, Paper, Divider, CircularProgress } from "@mui/material";
import { Box } from "@mui/system";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { useEffect, useState } from "react";
import { getStockValue, getStockValueRange, StockValuePoint } from "src/services/stock-value.service";
import { currencyFormatter } from "src/util";
import { subMonths, subDays, startOfMonth, endOfMonth, startOfQuarter, endOfQuarter } from "date-fns";
import StockValueChart from "src/components/stock-value-chart.component";

type TimeRange = 'month' | 'quarter' | 'sixMonths' | 'year';

const StockValueView = () => {
    const [date, setDate] = useState<Date>(startOfMonth(new Date()));
    const [stockValue, setStockValue] = useState<number>(0);
    const [chartData, setChartData] = useState<StockValuePoint[]>([]);
    const [selectedTimeRange, setSelectedTimeRange] = useState<TimeRange>('sixMonths');
    const [chartLoading, setChartLoading] = useState<boolean>(true);

    useEffect(() => {
        getStockValue(date, (value) => setStockValue(value), (e) => console.error(e));
    }, [date]);

    useEffect(() => {
        loadChartData(selectedTimeRange);
    }, [selectedTimeRange]);

    function _dateChanged(e: Date) {
        setDate(e);
    }

    function getDateRange(timeRange: TimeRange): { startDate: Date; endDate: Date } {
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        
        switch (timeRange) {
            case 'month':
                return {
                    startDate: startOfMonth(subMonths(today, 1)),
                    endDate: today
                };
            case 'quarter':
                return {
                    startDate: startOfQuarter(today),
                    endDate: today
                };
            case 'sixMonths':
                return {
                    startDate: subMonths(today, 6),
                    endDate: today
                };
            case 'year':
                return {
                    startDate: subMonths(today, 12),
                    endDate: today
                };
            default:
                return {
                    startDate: subMonths(today, 6),
                    endDate: today
                };
        }
    }

    function loadChartData(timeRange: TimeRange) {
        setChartLoading(true);
        const { startDate, endDate } = getDateRange(timeRange);
        
        getStockValueRange(
            startDate,
            endDate,
            (data) => {
                setChartData(data);
                setChartLoading(false);
            },
            (e) => {
                console.error('Error loading chart data:', e);
                setChartData([]);
                setChartLoading(false);
            }
        );
    }

    function handleTimeRangeChange(timeRange: TimeRange) {
        setSelectedTimeRange(timeRange);
    }

    return (
        <>
            <Typography variant="h1" gutterBottom>Stock Value</Typography>
            
            <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
                <Grid container spacing={3} alignItems="center">
                    <Grid size={6}>
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <DatePicker
                                label="Select Date"
                                value={date}
                                onChange={(value) => value && _dateChanged(value)}
                                format="dd-MM-yyyy"
                                maxDate={new Date()}
                            />
                        </LocalizationProvider>
                    </Grid>
                    <Grid size={6}>
                        <Box sx={{ textAlign: 'right' }}>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                Stock Value for {date.toLocaleDateString()}
                            </Typography>
                            <Typography variant="h4" color="primary">
                                {currencyFormatter.format(stockValue / 100)}
                            </Typography>
                        </Box>
                    </Grid>
                </Grid>
            </Paper>

            <Paper elevation={2} sx={{ p: 3 }}>
                <Box sx={{ mb: 3 }}>
                    <Typography variant="h2" gutterBottom>
                        Stock Value Trend
                    </Typography>
                    <ButtonGroup variant="outlined" size="small">
                        <Button
                            onClick={() => handleTimeRangeChange('month')}
                            variant={selectedTimeRange === 'month' ? 'contained' : 'outlined'}
                        >
                            Last Month
                        </Button>
                        <Button
                            onClick={() => handleTimeRangeChange('quarter')}
                            variant={selectedTimeRange === 'quarter' ? 'contained' : 'outlined'}
                        >
                            Last Quarter
                        </Button>
                        <Button
                            onClick={() => handleTimeRangeChange('sixMonths')}
                            variant={selectedTimeRange === 'sixMonths' ? 'contained' : 'outlined'}
                        >
                            6 Months
                        </Button>
                        <Button
                            onClick={() => handleTimeRangeChange('year')}
                            variant={selectedTimeRange === 'year' ? 'contained' : 'outlined'}
                        >
                            Last Year
                        </Button>
                    </ButtonGroup>
                </Box>

                <Divider sx={{ mb: 2 }} />

                <StockValueChart 
                    data={chartData} 
                    loading={chartLoading}
                />
            </Paper>
        </>
    );
}

export default StockValueView;